import { TagAllowedEntry, TagAllowedNode, TagSchemaField } from '@/core/notifications/model';

export function normalizeAllowedEntry(entry: TagAllowedEntry): TagAllowedNode {
  return typeof entry === 'string' ? { key: entry } : entry;
}

// group nodes are UI-only aggregators; only leaves are publishable/filterable values
export function isGroupNode(node: TagAllowedNode): boolean {
  return node.children !== undefined;
}

export function entryLeafKeys(entry: TagAllowedEntry): string[] {
  const node = normalizeAllowedEntry(entry);
  if (!node.children) return [node.key];
  return node.children.flatMap(entryLeafKeys);
}

export function fieldLeafKeys(field: TagSchemaField): string[] {
  return (field.allowed ?? []).flatMap(entryLeafKeys);
}

export function isFlatAllowedList(field: TagSchemaField): boolean {
  return (field.allowed ?? []).every(entry => !isGroupNode(normalizeAllowedEntry(entry)));
}

export function buildKeyNameMap(field: TagSchemaField): Map<string, string> {
  const map = new Map<string, string>();
  const walk = (entry: TagAllowedEntry) => {
    const node = normalizeAllowedEntry(entry);
    map.set(node.key, node.name ?? node.key);
    node.children?.forEach(walk);
  };
  (field.allowed ?? []).forEach(walk);
  return map;
}

export function leafLabel(field: TagSchemaField | undefined, key: string): string {
  if (!field) return key;
  return buildKeyNameMap(field).get(key) ?? key;
}

// Each app registers its own topics, so a field's `name` arrives with
// whatever capitalization that app happened to use ("region" vs "Region" vs
// "REGION") — there's no practical way to enforce consistency at the source
// across every app/team, so both helpers below normalize it at display time
// instead of trusting the source string's casing.

// For a field name embedded mid-sentence ("Receive every {name}") — always
// lowercase, so it reads naturally regardless of source casing.
export function fieldSentenceLabel(field: TagSchemaField | undefined, tagKey: string): string {
  return (field?.name ?? tagKey).toLowerCase();
}

// For a field name shown standalone, as a label/header/badge rather than
// inside a sentence (ScopeSummary's row title, TagFieldCard's header, an
// alarm row's "Filtering by" list, a coverage-gap summary) — capitalizes
// just the first letter, so "region" and "Region" both render as "Region".
// Deliberately doesn't touch the rest of the string (no full Title Case):
// re-casing every word would also mangle legitimate acronyms a field name
// might already contain correctly, like "Series ID" → "Series Id".
export function fieldHeaderLabel(field: TagSchemaField | undefined, tagKey: string): string {
  const raw = field?.name ?? tagKey;
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}
